import { useMutation, useQuery } from "@apollo/client";
import { compact, isArray, values } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { IoWarningOutline } from "react-icons/io5";
import {
  EngagementSurveyReportGroupBy,
  EngagementSurveyRollupType,
  ExportEngagementSurveyToCsvMutationMutation,
  ExportEngagementSurveyToCsvMutationMutationVariables,
  GetComplianceProgramForReportQuery,
  GetComplianceProgramForReportQueryVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import RecipientForm from "@apps/artifact/components/recipient-form";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Button, { ButtonSize, buttonTheme } from "@components/button/button";
import FormFields from "@components/form/form-fields";
import Layout from "@components/layout/layout";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import MotionDiv from "@components/motion/motion-div";
import { Select } from "@components/select/select";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";
import { capitalize, pluralize } from "@helpers/string";

import exportEngagementSurveyCsvMutation from "../graphql/export-engagement-survey-csv-mutation";
import getComplianceProgramForReportQuery from "../graphql/get-compliance-program-for-report-query";
import EngagementSurveyReportTable, {
  EngagementSurveyReportTableType,
  npsColors,
  rangeColors,
} from "./engagement-survey-report-table";
import TeamPicker, { TeamPickerTeam } from "./team-picker";

export const stickyColWidth = "w-92 min-w-92 max-w-92";
export const resultColWidth = "w-48 max-w-48 min-w-48";

const EngagementSurveyReport = ({
  complianceProgramId,
  headerContent,
  isInReporting,
}: {
  complianceProgramId: number;
  headerContent?: JSX.Element;
  isInReporting?: boolean;
}) => {
  const isAdmin = isAdminVar();
  const label = useLabel();
  const currentUser = currentUserVar();
  const [groupBy, setGroupBy] = useState<EngagementSurveyReportGroupBy>(
    EngagementSurveyReportGroupBy.Team
  );
  const [filters, setFilters] = useState<{
    showFilters: boolean;
    reportsToFilterList: BasicUser[];
    teamFilterList: TeamPickerTeam[];
  }>({ showFilters: false, reportsToFilterList: [], teamFilterList: [] });

  const filterCount = useMemo(() => {
    return values(filters).reduce((acc, filter) => {
      if (isArray(filter)) {
        return acc + filter.length;
      }
      return acc;
    }, 0);
  }, [filters]);

  const { data: programData, loading: isLoadingProgram } = useQuery<
    GetComplianceProgramForReportQuery,
    GetComplianceProgramForReportQueryVariables
  >(getComplianceProgramForReportQuery, {
    variables: {
      complianceProgramId,
      isPerformanceAssessmentReport: false,
      isUpwardAssessmentReport: false,
      isEngagementSurveyReport: true,
      engagementSurveyReportGroupBy: groupBy,
      engagementSurveyReportTeamIds:
        filters.teamFilterList.length > 0
          ? filters.teamFilterList.map((team) => team.id)
          : undefined,
      engagementSurveyReportManagerIds:
        filters.reportsToFilterList.length > 0
          ? filters.reportsToFilterList.map((manager) => manager.id)
          : undefined,
    },
  });

  const [exportToCSV, { loading: isExporting }] = useMutation<
    ExportEngagementSurveyToCsvMutationMutation,
    ExportEngagementSurveyToCsvMutationMutationVariables
  >(exportEngagementSurveyCsvMutation);

  const complianceProgram = useMemo(
    () => (programData ? programData?.complianceProgram : null),
    [programData]
  );

  const exportProgram = useCallback(() => {
    if (!complianceProgram?.engagementSurveyReport || isExporting) {
      return;
    }
    exportToCSV({
      variables: {
        complianceProgramId,
        groupBy,
        teamIds: filters.teamFilterList.map((team) => team.id),
        managerIds: filters.reportsToFilterList.map((manager) => manager.id),
      },
    });
    successNotificationVar({
      title: `CSV export will be emailed to ${currentUser.email}. The link will expire in two hours.`,
    });
  }, [
    complianceProgram?.engagementSurveyReport,
    complianceProgramId,
    currentUser.email,
    exportToCSV,
    groupBy,
    isExporting,
    filters,
  ]);

  const responders = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.responders.edges.map((edge) =>
          assertNonNull(edge?.node)
        )
      : [];
  }, [complianceProgram]);

  const hasResponses = useMemo(() => {
    const orgRollup = responders.find(
      ({ rollupType }) => rollupType === EngagementSurveyRollupType.Organization
    );
    return orgRollup && orgRollup.responderCount > 0;
  }, [responders]);

  const npsQuestions = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.npsQuestionRows.edges.map(
          (edge) => assertNonNull(edge?.node)
        )
      : [];
  }, [complianceProgram]);
  const rangeQuestions = useMemo(() => {
    return complianceProgram?.engagementSurveyReport
      ? complianceProgram.engagementSurveyReport?.rangeQuestionRows.edges.map(
          (edge) => assertNonNull(edge?.node)
        )
      : [];
  }, [complianceProgram]);

  if (
    !isLoadingProgram &&
    npsQuestions.length === 0 &&
    rangeQuestions.length === 0
  ) {
    return (
      <Layout.MainSubSectionEmpty>
        <div className="flex items-center justify-center gap-2">
          <TopicflowIcon type="info" className="h-4 w-4" />
          <div>
            There are no range or NPS questions in this survey. To view all the
            responses,{" "}
            <AppLink
              className="text-blue-600 hover:underline"
              to={`/survey/delivery/${complianceProgram?.assessmentDeliveries?.edges[0]?.node?.id}`}
            >
              view the full survey here
            </AppLink>
            .
          </div>
        </div>
      </Layout.MainSubSectionEmpty>
    );
  }

  return (
    <div
      className={classNames(
        "pb-6 flex flex-col gap-6",
        isInReporting && "px-8"
      )}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {headerContent}
          <Select
            prefix="Group by"
            options={compact([
              {
                label: label("team", { capitalize: true }),
                value: EngagementSurveyReportGroupBy.Team,
              },
              {
                label: "Reports to",
                value: EngagementSurveyReportGroupBy.Manager,
              },
              {
                label: "ICs vs Managers",
                value: EngagementSurveyReportGroupBy.IsManager,
              },
              {
                label: "Career track",
                value: EngagementSurveyReportGroupBy.CareerTrack,
              },
            ])}
            value={groupBy}
            disabled={isLoadingProgram}
            onChange={(opt) => setGroupBy(opt.value)}
          />

          <Button
            className="flex items-center"
            disabled={isLoadingProgram}
            theme={buttonTheme.text}
            onClick={() =>
              setFilters({ ...filters, showFilters: !filters.showFilters })
            }
          >
            <TopicflowIcon type="filter" className="h-5 w-5 mr-1" />
            {filters.showFilters ? "Hide filters" : "Show filters"}
            {filterCount > 0 && (
              <span className="ml-1 text-sm italic text-gray-500">
                ({filterCount} {pluralize("filter", filterCount)} active)
              </span>
            )}
          </Button>
        </div>

        <div className="flex items-center gap-3">
          {!isLoadingProgram &&
            !isAdmin &&
            !complianceProgram?.hasAccessToAppliesToUsers && (
              <Tooltip
                text={`You do not have the access privileges to view all the responses within the ${label(
                  "organization"
                )}.`}
              >
                <div className="text-sm bg-amber-100 py-2 px-3 rounded-md flex items-center gap-2">
                  <IoWarningOutline className="text-amber-700 h-5 w-5" />
                  <div>Not all responses are shown</div>
                </div>
              </Tooltip>
            )}
          {!isLoadingProgram && hasResponses && (
            <Button
              size={ButtonSize.large}
              onClick={exportProgram}
              text="Export CSV"
            />
          )}
          {!isLoadingProgram &&
            complianceProgram?.assessmentDeliveries?.edges &&
            complianceProgram?.assessmentDeliveries.edges.length > 0 && (
              <Button
                size={ButtonSize.large}
                to={`/survey/delivery/${complianceProgram?.assessmentDeliveries.edges[0]?.node?.id}`}
                text="View Full Data"
              />
            )}
        </div>
      </div>
      {!isLoadingProgram && filters.showFilters && (
        <MotionDiv
          className="pb-6"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="border rounded-md px-6 py-4">
            <FormFields
              fields={[
                {
                  title: "Reports to",
                  render: () => (
                    <RecipientForm
                      canChange
                      showCurrentUser
                      recipients={filters.reportsToFilterList}
                      onAddRecipient={(user) =>
                        setFilters({
                          ...filters,
                          reportsToFilterList: [
                            ...filters.reportsToFilterList,
                            user,
                          ],
                        })
                      }
                      onRemoveRecipient={(user) =>
                        setFilters({
                          ...filters,
                          reportsToFilterList:
                            filters.reportsToFilterList.filter(
                              (u) => u.id !== user.id
                            ),
                        })
                      }
                    />
                  ),
                },
                {
                  title: `${pluralize(capitalize(label("team")), 2)}`,
                  render: () => (
                    <TeamPicker
                      canChange
                      teams={filters.teamFilterList}
                      onAddTeam={(team) =>
                        setFilters({
                          ...filters,
                          teamFilterList: [...filters.teamFilterList, team],
                        })
                      }
                      onRemoveTeam={(team) =>
                        setFilters({
                          ...filters,
                          teamFilterList: filters.teamFilterList.filter(
                            (t) => t.id !== team.id
                          ),
                        })
                      }
                    />
                  ),
                },
              ]}
            />
          </div>
        </MotionDiv>
      )}
      {isLoadingProgram && (
        <div className="flex-1 flex justify-center">
          <Loading />
        </div>
      )}
      {!isLoadingProgram && !hasResponses && (
        <Layout.MainSubSectionEmpty>
          There are no responses to the survey.
        </Layout.MainSubSectionEmpty>
      )}
      {hasResponses && npsQuestions.length > 0 && !isLoadingProgram && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-semibold">eNPS Results</div>
            <div className="flex items-center gap-2">
              <div className="text-xs">Low</div>
              {npsColors.map((color) => (
                <div
                  key={color}
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
              <div className="text-xs">High</div>
            </div>
          </div>
          <EngagementSurveyReportTable
            responders={responders}
            questionRows={npsQuestions}
            type={EngagementSurveyReportTableType.NPS}
          />
        </div>
      )}
      {hasResponses && rangeQuestions.length > 0 && !isLoadingProgram && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div className="text-sm font-semibold">Range Question Results</div>
            <div className="flex items-center gap-2">
              <div className="text-xs">Low</div>
              {rangeColors.map((color) => (
                <div
                  key={color}
                  className="h-3 w-3 rounded-sm"
                  style={{ backgroundColor: color }}
                />
              ))}
              <div className="text-xs">High</div>
            </div>
          </div>
          <EngagementSurveyReportTable
            responders={responders}
            questionRows={rangeQuestions}
            type={EngagementSurveyReportTableType.RANGE}
          />
        </div>
      )}
    </div>
  );
};

export default EngagementSurveyReport;
